import { Product, ProductSearchFilters, ProductSort, Supplier } from "../types/api";

import { Button, Grid, Typography } from "@mui/material";

import { Box } from "@mui/system";
import { useCallback, useContext, useEffect, useState } from "react";
import Search from "../components/search.component";
import ProductTable from "../components/product-table.component";
import ProductFilterChips, {
  EMPTY_PRODUCT_FILTERS,
} from "../components/product-filter-chips.component";
import { deleteProduct, fetchProducts, moveProductsToLocation, updateProductsSupplier, updateProductsTags } from "../services/product.service";
import MoveLocationModal from "../components/modal.move-location";
import UpdateSupplierModal from "../components/modal.update-supplier";
import { useLocations } from "src/hooks/useLocations";
import { getSuppliers } from "../services/supplier.service";
import { useNavigate } from "react-router-dom";
import { EditProductRoute, ViewProductRoute } from "src/routes";
import { useStickyState } from "src/util";
import GlobalMessageContext from "src/context/globalMessage.context";
import TagEditor from "src/components/product-tag-editor";
import ButtonWithConfirm from "src/components/modal.confirm-dialog.component";
import { GridRowSelectionModel } from "@mui/x-data-grid";

const ProductsView = () => {
  const [productData, setProductData] = useState<Product[]>([]);
  const [currentSkip, setCurrentSkip] = useStickyState<number>(
    0,
    "product.currentSkip"
  );
  const [currentTake, setCurrentTake] = useStickyState<number>(
    25,
    "product.currentTake"
  );
  const [totalRows, setTotalRows] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useStickyState<string>(
    "",
    "product.searchTerm"
  );
  const [filters, setFilters] = useStickyState<ProductSearchFilters>(
    { ...EMPTY_PRODUCT_FILTERS },
    "product.advancedFilters"
  );
  // Sticky like the filters, so the chosen order survives navigating away
  // to a product and back. null = the server's default order.
  const [sort, setSort] = useStickyState<ProductSort | null>(null, "product.sort");

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const uxMessage = useContext(GlobalMessageContext);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const { refresh: refreshLocations } = useLocations();

  // Load suppliers on component mount
  useEffect(() => {
    getSuppliers({}, setSuppliers, () => {}, () => {});
    // Earlier versions persisted the whole product page here, which could be
    // several MB. Clear it so it no longer counts against the storage quota.
    try {
      window.localStorage.removeItem("product.productData");
      window.localStorage.removeItem("product.totalRows");
    } catch {
      /* ignore */
    }
  }, []);

  // Single loader for the product list. The search term (if any) and the
  // advanced filters are both applied server-side, so every change to page,
  // page size, term or filters results in exactly one request.
  const loadProducts = useCallback(() => {
    setLoading(true);
    fetchProducts(
      { skip: currentSkip, take: currentTake, search: searchTerm, filters, sort },
      (data) => {
        setLoading(false);
        setProductData(data);
      },
      setTotalRows,
      (error) => {
        setLoading(false);
        setProductData([]);
        console.error('Failed to load products:', error);
        uxMessage.setMessage({
          message: "Sorry - we seem to be having some troubles right now... please try again later!",
          severity: "error",
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSkip, currentTake, searchTerm, filters, sort]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const setSkip = (page, details) => {
    setCurrentSkip(page * currentTake);    
  };

  const setTake = (pageSize, details) => {
    if (pageSize !== currentTake) {
      // Only recalculate skip if page size actually changed
      const oldTake = currentTake;
      setCurrentTake(pageSize);
      setCurrentSkip(Math.trunc(currentSkip * (oldTake / pageSize)));
    }
  };

  // A new term or new filters always start from the first page; otherwise a
  // search made while on page 5 of the full list would ask for page 5 of the
  // (much smaller) result set and come back empty.
  const doSearch = (st: string) => {
    if (st === searchTerm) {
      loadProducts();
      return;
    }
    setCurrentSkip(0);
    setSearchTerm(st);
  };

  const resetSearch = () => {
    if (searchTerm === "") return;
    setCurrentSkip(0);
    setSearchTerm("");
  };

  const changeFilters = (next: ProductSearchFilters) => {
    setCurrentSkip(0);
    setFilters(next);
  };

  // A new sort order also starts from the first page. The grid can report the
  // sort it already has (e.g. after a page of rows arrives), which must not
  // touch the page.
  const changeSort = (next: ProductSort | null) => {
    if (next?.field === sort?.field && next?.dir === sort?.dir) return;
    setCurrentSkip(0);
    setSort(next);
  };

  const _onSelectProduct = (p: Product) => {
    navigate(ViewProductRoute, { state: { selectedProduct: p } });
  };

  const _onDelete = (tags: string[]) => {
    _updateProductTags(tags, true)
  }

  const _onAddTags = (newTags: string[]) => {
    _updateProductTags(newTags, false);
  }

  // Bulk tag change for the ticked products on this page: one request. The
  // server updates the database and re-syncs published products to Shopify
  // in the background, so the list is refreshed straight away.
  const _updateProductTags = (tags: string[], toDelete: boolean) => {
    const selectedProducts = getSelectedProducts();
    if (selectedProducts.length === 0 || tags.length === 0) return;

    // Optimistic update for immediate feedback
    const selectedIds = new Set(selectedProducts.map((p) => p.id));
    setProductData(
      productData.map((product: Product) => {
        if (!selectedIds.has(product.id)) return product;
        const current = product.tags || [];
        return {
          ...product,
          tags: toDelete
            ? current.filter((t: string) => !tags.includes(t))
            : [...new Set([...current, ...tags])],
        };
      })
    );

    const guids = selectedProducts.map((p) => p.guid).filter(Boolean) as string[];
    updateProductsTags(guids, toDelete ? [] : tags, toDelete ? tags : [])
      .then((result) => {
        const n = result.count;
        const sync =
          result.shopifySyncQueued > 0
            ? ` Syncing ${result.shopifySyncQueued} to Shopify in the background.`
            : '';
        uxMessage.setMessage({
          message: `Updated tags on ${n} product${n !== 1 ? 's' : ''}.${sync}`,
          severity: 'success',
        });
        loadProducts();
      })
      .catch((e) => {
        console.error('Failed to update product tags:', e);
        uxMessage.setMessage({
          message: e?.response?.data?.message || 'Failed to update tags. Please try again.',
          severity: 'error',
        });
        loadProducts();
      });
  };

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set()
  })

  // Untick everything whenever the rows in the table change, so a bulk action
  // can't act on products that were ticked (or "select all"-ed) under a
  // different search, filter, sort or page.
  useEffect(() => {
    setRowSelectionModel({ type: 'include', ids: new Set() });
  }, [currentSkip, currentTake, searchTerm, filters, sort]);

  // Resolve the currently selected products from the selection model (handles
  // both "include" and "exclude" model types, matching the tag editor logic).
  const getSelectedProducts = (): Product[] => {
    if (!productData || !rowSelectionModel || !rowSelectionModel.ids) return [];
    return rowSelectionModel.type === 'include'
      ? ([...rowSelectionModel.ids]
          .map((id) => productData.find((p: Product) => p.id === id))
          .filter(Boolean) as Product[])
      : productData.filter((p: Product) => !rowSelectionModel.ids.has(p.id));
  };

  const selectedCount = getSelectedProducts().length;

  // Bulk delete: delete each selected product via the standard per-product
  // delete workflow (which also removes it from Shopify), fired in parallel.
  const _deleteSelectedProducts = () => {
    const selectedProducts = getSelectedProducts();
    if (selectedProducts.length === 0) return;

    // Optimistically remove the deleted products from the list for instant
    // feedback, then reconcile with the server below.
    const selectedIds = new Set(selectedProducts.map((p) => p.id));
    setProductData(productData.filter((p: Product) => !selectedIds.has(p.id)));
    setRowSelectionModel({ type: 'include', ids: new Set() });

    const deletePromises = selectedProducts.map((p: Product) => {
      return new Promise((resolve, reject) => {
        deleteProduct({
          product: p,
          success: () => resolve(p),
          failure: () => reject(p),
        });
      });
    });

    Promise.allSettled(deletePromises).then((results) => {
      const failures = results.filter((r) => r.status === 'rejected');
      const successes = results.filter((r) => r.status === 'fulfilled');

      // Refresh to reflect true server state.
      loadProducts();

      if (failures.length === results.length) {
        uxMessage.setMessage({
          message: 'Failed to delete products. Please try again.',
          severity: 'error',
        });
      } else if (failures.length > 0) {
        uxMessage.setMessage({
          message: `Deleted ${successes.length}/${results.length} products`,
          severity: 'warning',
        });
      } else {
        uxMessage.setMessage({
          message: `Successfully deleted ${results.length} product(s)!`,
          severity: 'success',
        });
      }
    });
  };

  // Bulk move: one request for all ticked products on this page. Location is
  // not held in Shopify, so there is no per-product sync.
  const _moveSelectedProducts = (location: string) => {
    const selectedProducts = getSelectedProducts();
    setMoveDialogOpen(false);
    if (selectedProducts.length === 0) return;

    const selectedIds = new Set(selectedProducts.map((p) => p.id));
    setProductData(
      productData.map((p: Product) => (selectedIds.has(p.id) ? { ...p, location } : p))
    );
    setRowSelectionModel({ type: 'include', ids: new Set() });

    const guids = selectedProducts.map((p) => p.guid).filter(Boolean) as string[];
    moveProductsToLocation(guids, location)
      .then((result) => {
        uxMessage.setMessage({
          message: `Moved ${result.count} product${result.count !== 1 ? 's' : ''} to ${result.location}`,
          severity: 'success',
        });
        refreshLocations();
        loadProducts();
      })
      .catch((e) => {
        console.error('Failed to move products:', e);
        uxMessage.setMessage({
          message: e?.response?.data?.message || 'Failed to move products. Please try again.',
          severity: 'error',
        });
        loadProducts();
      });
  };

  // Bulk supplier change: one request for all ticked products on this page.
  // Supplier is not synced to Shopify or Xero, so there is no per-product sync.
  const _updateSelectedSupplier = (supplier: Supplier) => {
    const selectedProducts = getSelectedProducts();
    setSupplierDialogOpen(false);
    if (selectedProducts.length === 0 || !supplier.guid) return;

    const selectedIds = new Set(selectedProducts.map((p) => p.id));
    setProductData(
      productData.map((p: Product) =>
        selectedIds.has(p.id) ? { ...p, supplierGuid: supplier.guid } : p
      )
    );
    setRowSelectionModel({ type: 'include', ids: new Set() });

    const guids = selectedProducts.map((p) => p.guid).filter(Boolean) as string[];
    updateProductsSupplier(guids, supplier.guid)
      .then((result) => {
        uxMessage.setMessage({
          message: `Updated the supplier of ${result.count} product${result.count !== 1 ? 's' : ''} to ${result.supplier.name}`,
          severity: 'success',
        });
        loadProducts();
      })
      .catch((e) => {
        console.error('Failed to update product supplier:', e);
        uxMessage.setMessage({
          message: e?.response?.data?.message || 'Failed to update the supplier. Please try again.',
          severity: 'error',
        });
        loadProducts();
      });
  };

  return (
    <>
      <Typography variant="h1">Products</Typography>
      <Box sx={{ m: 1, textAlign: "right" }}>
        {selectedCount > 0 && (
          <ButtonWithConfirm
            variant="contained"
            size="small"
            color="error"
            sx={{ mr: 1 }}
            okButtonLabel="Delete"
            message={`This will permanently delete ${selectedCount} product${
              selectedCount !== 1 ? "s" : ""
            } and ${
              selectedCount !== 1 ? "their" : "it's"
            } variants. This cannot be undone!`}
            onOk={_deleteSelectedProducts}
          >
            {`Delete Product${selectedCount !== 1 ? "s" : ""} (${selectedCount})`}
          </ButtonWithConfirm>
        )}
        {selectedCount > 0 && (
          <Button
            variant="contained"
            size="small"
            color="inherit"
            sx={{ mr: 1 }}
            onClick={() => setMoveDialogOpen(true)}
            data-testid="move-products-button"
          >
            {`Move Product${selectedCount !== 1 ? "s" : ""} (${selectedCount})`}
          </Button>
        )}
        {selectedCount > 0 && (
          <Button
            variant="contained"
            size="small"
            color="inherit"
            sx={{ mr: 1 }}
            onClick={() => setSupplierDialogOpen(true)}
            data-testid="update-supplier-button"
          >
            {`Update Supplier (${selectedCount})`}
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          color="inherit"
          onClick={() =>
            navigate(EditProductRoute, {
              state: { selectedProduct: undefined, createNew: true },
            })
          }
        >
          Create Product
        </Button>
      </Box>
      <Box sx={{ m: 1 }}>
        <div style={{ textAlign: "center" }}>
          <Search
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            doSearch={(st: string) => doSearch(st)}
            clearOnEnter={false}
            onReset={resetSearch}
          />
          <ProductFilterChips
            filters={filters}
            onChange={changeFilters}
            suppliers={suppliers.filter((s: Supplier) => !s.archivedAt)}
          />
        </div>
      </Box>
      <Box sx={{ m: 3 }}></Box>
      <Box sx={{ m: 1 }}>
        <ProductTable
          loading={loading}
          productData={productData}
          suppliers={suppliers}
          pageSize={currentTake}
          totalRows={totalRows}
          currentPage={Math.floor(currentSkip / currentTake)}
          onSelectProduct={_onSelectProduct}
          onPageChange={setSkip}
          onPageSizeChange={setTake}
          showChecks={true}
          onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
          rowSelectionModel={rowSelectionModel}
          sort={sort}
          onSortChange={changeSort}
        />
      </Box>
      {(() => {
        if (!rowSelectionModel || !rowSelectionModel.ids) return false;

        const modelType = rowSelectionModel.type;
        const idsSize = rowSelectionModel.ids.size;

        // For "include" model: show if there are selected IDs
        // For "exclude" model: show if not all are excluded (i.e., some are selected)
        return modelType === 'include'
          ? idsSize > 0
          : idsSize < (productData?.length || 0);
      })() && (
        <Box sx={{ m: 1 }}>
          <TagEditor
            products={(() => {
              if (!rowSelectionModel || !productData) return [];

              // For "include" model: use the IDs in the set
              if (rowSelectionModel.type === 'include') {
                return [...rowSelectionModel.ids].map(id =>
                  productData.find((p:Product) => p.id === id)
                ).filter(Boolean);
              }

              // For "exclude" model: use all products except those in the set
              return productData.filter((p:Product) =>
                !rowSelectionModel.ids.has(p.id)
              );
            })()}
            onDelete={_onDelete}
            onAdd={_onAddTags}
          />
        </Box>
      )}
      <MoveLocationModal
        open={moveDialogOpen}
        productCount={selectedCount}
        onCancel={() => setMoveDialogOpen(false)}
        onMove={_moveSelectedProducts}
      />
      <UpdateSupplierModal
        open={supplierDialogOpen}
        productCount={selectedCount}
        onCancel={() => setSupplierDialogOpen(false)}
        onUpdate={_updateSelectedSupplier}
      />
    </>
  );
};

export default ProductsView;
